import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Helmet } from "react-helmet-async";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useUpload } from "../context/UploadContext";
import CONFIG from "../config";
import BRANDING from "../branding";
import SEO from "../components/common/SEO";

export default function EditItem() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const { addTask } = useUpload();
  const isArabic = i18n.language?.startsWith('ar');

  const [item, setItem] = useState(null);
  const [categories, setCategories] = useState([]);
  const [existingFilters, setExistingFilters] = useState({ color: [], material: [], dimensions: [] });

  const [previewMain, setPreviewMain] = useState(null);
  const [existingThumbs, setExistingThumbs] = useState([]);
  const [newThumbs, setNewThumbs] = useState([]);
  const [newMainImage, setNewMainImage] = useState(null);
  const [newVideo, setNewVideo] = useState(null);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const compressImage = async (file, maxWidth = 1200, quality = 0.7) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target.result;
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;

          if (width > maxWidth) {
            height = (maxWidth / width) * height;
            width = maxWidth;
          }

          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);

          canvas.toBlob((blob) => {
            const compressedFile = new File([blob], file.name.split('.')[0] + '.webp', {
              type: 'image/webp',
              lastModified: Date.now(),
            });
            resolve(compressedFile);
          }, 'image/webp', quality);
        };
      };
    });
  };


  const loadFilters = useCallback(async () => {
    try {
      const res = await axios.get(`${CONFIG.API_URL}/api/products/filters`);
      setExistingFilters({
        color: res.data?.color || [],
        material: res.data?.material || [],
        dimensions: res.data?.dimensions || []
      });
    } catch (err) {
      console.log("Error loading filters", err);
    }
  }, []);

  const loadItem = useCallback(async () => {
    try {
      const res = await axios.get(`${CONFIG.API_URL}/api/products/${id}`);
      const product = res.data;
      setItem({
        ...product,
        category: (product.categories || []).map(c => c.id),
        newCategory: ""
      });

      if (product.imageUrl) setPreviewMain(product.imageUrl);
      const thumbs = [
        ...(product.thumbnailUrls || []),
        product.thumbnail1,
        product.thumbnail2,
        product.thumbnail3,
        product.thumbnail4,
        product.thumbnail5
      ].filter(Boolean);
      setExistingThumbs([...new Set(thumbs)]);
    } catch (err) {
      console.error(err);
    }
  }, [id]);

  const loadCategories = useCallback(async () => {
    try {
      const res = await axios.get(`${CONFIG.API_URL}/api/categories`);
      setCategories(res.data || []);
    } catch (err) { }
  }, []);

  useEffect(() => {
    loadItem();
    loadCategories();
    loadFilters();

    // Check for retry data
    if (location.state?.retryData?.item) {
      setItem(prev => ({
        ...prev,
        ...location.state.retryData.item,
        newCategory: ""
      }));

      const initial = location.state.retryData.initialData;
      if (initial?.existingThumbs) setExistingThumbs(initial.existingThumbs);

      setMessage("💡 Form pre-filled from task history. Please re-select your new media files.");
    }
  }, [id, loadItem, loadCategories, loadFilters, location.state]);


  const handleThumbFiles = (files) => {
    const totalCurrent = existingThumbs.length + newThumbs.length;
    const available = 4 - totalCurrent;
    if (available <= 0) {
      setMessage("⚠️ Maximum 4 thumbnails reached.");
      return;
    }
    const toAdd = files.slice(0, available);
    setNewThumbs([...newThumbs, ...toAdd]);
  };

  const removeExistingThumb = (indexDisplay) => {
    const arr = [...existingThumbs];
    arr.splice(indexDisplay, 1);
    setExistingThumbs(arr);
  };

  const removeNewThumb = (index) => {
    const arr = [...newThumbs];
    arr.splice(index, 1);
    setNewThumbs(arr);
  };

  const handleAddCategory = async () => {
    if (!item.newCategory?.trim()) return;
    try {
      setLoading(true);
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const res = await axios.post(`${CONFIG.API_URL}/api/categories`,
        { name: item.newCategory, name_ar: item.newCategoryAr },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCategories(prev => {
        const exists = prev.find(c => c.id === res.data.id);
        if (exists) return prev;
        return [...prev, res.data].sort((a, b) => a.name.localeCompare(b.name));
      });
      setItem(prev => ({
        ...prev,
        category: [...(prev.category || []), res.data.id.toString()],
        newCategory: "",
        newCategoryAr: ""
      }));
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
      setMessage(`❌ Failed to add category: ${err.response?.data?.message || err.message}`);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (loading) return;
    try {
      setLoading(true);
      setMessage("⏳ Update added to background. You can continue browsing!");

      const initialData = { newMainImage, newVideo, newThumbs, existingThumbs };
      addTask('EDIT', { id, item, initialData, categories }, { compressImage });

      setLoading(false);
      setTimeout(() => navigate("/admin/product"), 2000);
    } catch (err) {
      console.error("Update failed:", err);
      setLoading(false);
      const status = err.response?.status;
      if (status === 401) {
        setMessage("❌ Session expired. Please log in again.");
      } else {
        setMessage(`❌ ${err.response?.data?.message || err.message || "Failed to start background update."}`);
      }
    }
  };

  const GroupTitle = ({ title, icon }) => (
    <div className="flex items-center gap-3 mb-6 pb-2 border-b border-gray-100">
      <div className="w-10 h-10 rounded-xl bg-brand-primary/10 flex items-center justify-center text-brand-primary">
        <i className={`fas ${icon}`}></i>
      </div>
      <h3 className="text-lg font-black text-gray-900 uppercase tracking-tight">{title}</h3>
    </div>
  );

  const MultiSelectField = ({ label, name, options, value, arValue, placeholder }) => {
    const [typed, setTyped] = useState("");
    const [typedAr, setTypedAr] = useState("");

    const addValue = () => {
      if (!typed.trim()) return;
      const newVal = value ? `${value}, ${typed.trim()}` : typed.trim();
      const newValAr = arValue ? `${arValue}، ${typedAr.trim() || typed.trim()}` : (typedAr.trim() || typed.trim());
      setItem(prev => ({ ...prev, [name]: newVal, [`${name}_ar`]: newValAr }));
      setTyped("");
      setTypedAr("");
    };

    const handleSelectExisting = (opt) => {
      const isSelected = value?.split(", ").includes(opt.name);
      if (isSelected) {
        const newVal = value.split(", ").filter(v => v !== opt.name).join(", ");
        const newValAr = arValue.split("، ").filter(v => v !== opt.name_ar).join("، ");
        setItem(prev => ({ ...prev, [name]: newVal, [`${name}_ar`]: newValAr }));
      } else {
        const newVal = value ? `${value}, ${opt.name}` : opt.name;
        const newValAr = arValue ? `${arValue}، ${opt.name_ar || opt.name}` : (opt.name_ar || opt.name);
        setItem(prev => ({ ...prev, [name]: newVal, [`${name}_ar`]: newValAr }));
      }
    };

    return (
      <div className="space-y-4 p-6 bg-gray-50/50 rounded-[30px] border border-gray-100">
        <label className="block text-xs font-black text-gray-400 uppercase tracking-widest px-1">{label}</label>
        <div className="flex flex-wrap gap-2 min-h-[40px]">
          {value?.split(", ").filter(Boolean).map((v, i) => (
            <span key={i} className="bg-white text-gray-900 px-4 py-2 rounded-xl text-xs font-bold shadow-sm border border-gray-100 flex items-center gap-2 group transition-all hover:border-red-200">
              {v}
              <button type="button" onClick={() => {
                const parts = value.split(", ");
                const partsAr = arValue.split("، ");
                parts.splice(i, 1);
                partsAr.splice(i, 1);
                setItem(prev => ({ ...prev, [name]: parts.join(", "), [`${name}_ar`]: partsAr.join("، ") }));
              }} className="text-gray-400 group-hover:text-red-500">×</button>
            </span>
          ))}
          {!value && <p className="text-xs text-gray-400 italic py-2">{isArabic ? "لا توجد سمات محددة" : "No attributes selected"}</p>}
        </div>
        <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto p-4 bg-white rounded-2xl border border-gray-100 shadow-inner">
          {options.map((opt, i) => {
            const isSelected = value?.split(", ").includes(opt.name);
            return (
              <button key={i} type="button" onClick={() => handleSelectExisting(opt)}
                className={`text-xs px-3 py-2 rounded-xl transition-all font-bold ${isSelected ? "bg-brand-primary text-white shadow-lg shadow-brand-primary/20" : "bg-gray-50 text-gray-500 hover:bg-gray-100"}`}>
                {isArabic ? (opt.name_ar || opt.name) : opt.name}
              </button>
            );
          })}
        </div>
        <div className="flex flex-col sm:flex-row gap-2 pt-2">
          <input type="text" placeholder={`${placeholder} (EN)`} value={typed} onChange={(e) => setTyped(e.target.value)}
            className="flex-1 text-sm p-4 border border-gray-100 rounded-2xl bg-white focus:ring-4 focus:ring-brand-primary/10 transition-all outline-none font-medium" />
          <input type="text" placeholder={`${placeholder} (AR)`} value={typedAr} onChange={(e) => setTypedAr(e.target.value)}
            className="flex-1 text-sm p-4 border border-gray-100 rounded-2xl bg-white focus:ring-4 focus:ring-brand-primary/10 transition-all outline-none font-medium" dir="rtl" />
          <button type="button" onClick={addValue} className="bg-gray-900 text-white px-6 py-4 rounded-2xl text-xs font-black uppercase tracking-widest hover:bg-brand-primary transition-all active:scale-95">
            {t('admin.common.add')}
          </button>
        </div>
      </div>
    );
  };

  if (!item) return (
    <div className="min-h-screen flex items-center justify-center bg-[#fcfcfc]">
      <div className="w-12 h-12 border-4 border-brand-primary border-t-transparent rounded-full animate-spin"></div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#fcfcfc] py-12 px-4 sm:px-6 lg:px-8">
      <Helmet>
        <html lang={isArabic ? "ar" : "en"} dir={isArabic ? "rtl" : "ltr"} />
        <title>{t('admin.common.edit')} | {isArabic ? BRANDING.appName.ar : BRANDING.appName.en}</title>
      </Helmet>
      <SEO />

      <div className="max-w-4xl mx-auto">
        <header className="flex items-center justify-between mb-10">
          <div>
            <h1 className="text-3xl font-black text-gray-900 tracking-tight">{t('admin.common.edit')}</h1>
            <p className="text-gray-500 font-medium mt-1">{t('admin.products_page.refine_masterpiece_detail')}</p>
          </div>
          <button onClick={() => navigate("/admin/product")} className="flex items-center gap-2 text-gray-500 hover:text-brand-primary transition-all text-sm font-black uppercase tracking-widest group">
            <span className={`transition-transform group-hover:-translate-x-1 ${isArabic ? "rotate-180 group-hover:translate-x-1" : ""}`}>← </span>{t('admin.common.back')}
          </button>
        </header>

        <form onSubmit={handleUpdate} className="space-y-8 bg-white p-8 sm:p-12 rounded-[40px] shadow-[0_20px_50px_rgba(0,0,0,0.03)] border border-gray-50">
          <section>
            <GroupTitle title={t('admin.products_page.form.info_section')} icon="fa-info-circle" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="text-xs font-black text-gray-400 uppercase tracking-widest px-1">{t('admin.products_page.form.name_en')}</label>
                <input type="text" value={item.name} onChange={(e) => setItem({ ...item, name: e.target.value })} className="w-full p-4 rounded-2xl border border-gray-100 bg-gray-50/50 focus:bg-white focus:ring-4 focus:ring-brand-primary/10 transition-all outline-none font-medium" required />
              </div>
              <div className="space-y-2 text-right">
                <label className="text-xs font-black text-gray-400 uppercase tracking-widest px-1">{t('admin.products_page.form.name_ar')}</label>
                <input type="text" value={item.name_ar || ""} onChange={(e) => setItem({ ...item, name_ar: e.target.value })} className="w-full p-4 rounded-2xl border border-gray-100 bg-gray-50/50 focus:bg-white focus:ring-4 focus:ring-brand-primary/10 transition-all outline-none font-medium text-right" dir="rtl" />
              </div>
              <div className="md:col-span-2 space-y-2">
                <label className="text-xs font-black text-gray-400 uppercase tracking-widest px-1">{t('admin.products_page.form.desc_en')}</label>
                <textarea value={item.description} onChange={(e) => setItem({ ...item, description: e.target.value })} className="w-full p-4 rounded-2xl border border-gray-100 bg-gray-50/50 focus:bg-white focus:ring-4 focus:ring-brand-primary/10 transition-all outline-none font-medium min-h-[120px]" required />
              </div>
              <div className="md:col-span-2 space-y-2 text-right">
                <label className="text-xs font-black text-gray-400 uppercase tracking-widest px-1">{t('admin.products_page.form.desc_ar')}</label>
                <textarea value={item.description_ar || ""} onChange={(e) => setItem({ ...item, description_ar: e.target.value })} className="w-full p-4 rounded-2xl border border-gray-100 bg-gray-50/50 focus:bg-white focus:ring-4 focus:ring-brand-primary/10 transition-all outline-none font-medium min-h-[120px] text-right" dir="rtl" />
              </div>
            </div>
          </section>

          <section>
            <GroupTitle title={t('admin.products_page.form.pricing_section')} icon="fa-tag" />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                { field: 'price', label: 'USD', icon: 'fa-dollar-sign' },
                { field: 'price_egp', label: 'EGP', icon: 'fa-pound-sign' },
                { field: 'price_sdg', label: 'SDG', icon: 'fa-coins' }
              ].map(p => (
                <div key={p.field} className="space-y-2">
                  <label className="text-xs font-black text-gray-400 uppercase tracking-widest px-1">{p.label}</label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"><i className={`fas ${p.icon}`}></i></span>
                    <input type="number" value={item[p.field] || ""} onChange={(e) => setItem({ ...item, [p.field]: e.target.value })} className="w-full pl-10 pr-4 py-4 rounded-2xl border border-gray-100 bg-gray-50/50 focus:bg-white focus:ring-4 focus:ring-brand-primary/10 transition-all outline-none font-black" />
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section>
            <GroupTitle title={t('admin.products_page.category')} icon="fa-layer-group" />
            <div className="space-y-6">
              <div className="space-y-2">
                <label className="text-xs font-black text-gray-400 uppercase tracking-widest px-1">{t('admin.products_page.form.categories_select')}</label>
                <select value={item.category} onChange={(e) => {
                  const opts = Array.from(e.target.options).filter(o => o.selected).map(o => o.value);
                  setItem({ ...item, category: opts });
                }} multiple className="w-full p-4 rounded-2xl border border-gray-100 bg-gray-50/50 focus:bg-white focus:ring-4 focus:ring-brand-primary/10 transition-all outline-none font-medium h-48 shadow-inner overflow-y-auto">
                  {(categories || []).map(c => (
                    <option key={c.id} value={c.id} className="p-3 rounded-xl m-1 transition-colors cursor-pointer font-bold">
                      {isArabic ? (c.name_ar || c.name) : c.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="p-6 bg-gray-50/50 rounded-[30px] border border-gray-100 space-y-4">
                <label className="block text-xs font-black text-gray-400 uppercase tracking-widest px-1">{t('admin.products_page.form.or_create_new_category')}</label>
                <div className="flex flex-col sm:flex-row gap-2">
                  <input type="text" placeholder={t('admin.products_page.form.new_category_en')} value={item.newCategory || ""} onChange={(e) => setItem({ ...item, newCategory: e.target.value })} className="flex-1 p-4 rounded-2xl border border-gray-100 bg-white focus:ring-4 focus:ring-brand-primary/10 transition-all outline-none font-medium text-sm" />
                  <input type="text" placeholder={t('admin.products_page.form.new_category_ar')} value={item.newCategoryAr || ""} onChange={(e) => setItem({ ...item, newCategoryAr: e.target.value })} className="flex-1 p-4 rounded-2xl border border-gray-100 bg-white focus:ring-4 focus:ring-brand-primary/10 transition-all outline-none font-medium text-sm text-right" dir="rtl" />
                  <button type="button" onClick={handleAddCategory} disabled={!item.newCategory?.trim() || loading} className="bg-gray-900 text-white px-6 py-4 rounded-2xl text-xs font-black uppercase tracking-widest hover:bg-brand-primary transition-all active:scale-95 disabled:bg-gray-300 disabled:cursor-not-allowed">
                    {t('admin.common.add')}
                  </button>
                </div>
              </div>
            </div>
          </section>

          <section>
            <GroupTitle title={t('admin.products_page.form.media_section')} icon="fa-images" />
            <div className="space-y-8">
              <div className="space-y-4">
                <label className="text-xs font-black text-gray-400 uppercase tracking-widest px-1">{t('admin.products_page.form.main_image')}</label>
                <div className="flex flex-col sm:flex-row gap-6 items-center">
                  <div className="w-40 h-40 rounded-3xl border-2 border-dashed border-gray-200 bg-gray-50 flex items-center justify-center overflow-hidden cursor-pointer hover:border-brand-primary transition-all group relative"
                    onClick={() => document.getElementById('main-image-input').click()}>
                    {previewMain ? <img src={previewMain} alt="preview" className="w-full h-full object-cover" /> : <i className="fas fa-camera text-2xl text-gray-300"></i>}
                    <input id="main-image-input" type="file" accept="image/*" onChange={(e) => { setNewMainImage(e.target.files[0]); setPreviewMain(URL.createObjectURL(e.target.files[0])); }} className="hidden" />
                  </div>
                  <div className="flex-1 text-center sm:text-left">
                    <p className="text-sm font-bold text-gray-700">{previewMain ? t('admin.products_page.form.main_image_updated') : t('admin.products_page.form.no_change')}</p>
                    <p className="text-xs text-gray-400 mt-1">{t('admin.products_page.form.leave_empty_to_keep_existing')}</p>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <label className="text-xs font-black text-gray-400 uppercase tracking-widest px-1">{t('admin.products_page.form.extra_images')}</label>
                <div className="p-8 border-2 border-dashed border-gray-200 rounded-[30px] bg-gray-50/50 text-center cursor-pointer hover:border-brand-primary transition-all group"
                  onClick={() => document.getElementById('edit-thumbnail-input').click()}>
                  <input id="edit-thumbnail-input" type="file" multiple accept="image/*" onChange={(e) => handleThumbFiles(Array.from(e.target.files))} className="hidden" />
                  <div className="flex flex-col items-center">
                    <div className="w-12 h-12 bg-white rounded-2xl shadow-sm flex items-center justify-center text-gray-400 group-hover:bg-brand-primary group-hover:text-white transition-all mb-4">
                      <i className="fas fa-images text-xl"></i>
                    </div>
                    <p className="font-black text-gray-900 uppercase tracking-widest text-xs mb-1">{t('admin.products_page.form.click_to_browse')}</p>
                    <p className="text-xs text-gray-400 font-medium">{existingThumbs.length + newThumbs.length}/4</p>
                  </div>
                </div>
                <div className="flex gap-4 flex-wrap mt-4">
                  {existingThumbs.map((src, i) => (
                    <div key={`old-${i}`} className="relative group/item w-24 h-24">
                      <img src={src} className="w-full h-full object-cover rounded-2xl border border-gray-100 shadow-sm" alt="thumb" />
                      <button type="button" onClick={() => removeExistingThumb(i)} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-8 h-8 flex items-center justify-center opacity-0 group-hover/item:opacity-100 transition-all shadow-lg">×</button>
                    </div>
                  ))}
                  {newThumbs.map((f, i) => (
                    <div key={`new-${i}`} className="relative group/item w-24 h-24">
                      <img src={URL.createObjectURL(f)} className="w-full h-full object-cover rounded-2xl border-2 border-brand-primary/20 shadow-sm" alt="new-thumb" />
                      <button type="button" onClick={() => removeNewThumb(i)} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-8 h-8 flex items-center justify-center shadow-lg">×</button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-4">
                <label className="text-xs font-black text-gray-400 uppercase tracking-widest px-1">{t('admin.products_page.form.video_optional')}</label>
                {item.videoUrl && !newVideo && <video src={item.videoUrl} controls className="h-40 rounded-2xl border border-gray-100" />}
                <input type="file" accept="video/*" onChange={(e) => setNewVideo(e.target.files[0])} className="w-full p-4 border border-gray-100 rounded-2xl bg-gray-50/50 file:mr-4 file:bg-gray-900 file:text-white file:rounded-xl file:border-0 file:px-4 file:py-2" />
              </div>
            </div>
          </section>

          <section>
            <GroupTitle title={t('admin.products_page.form.attributes_section')} icon="fa-sliders-h" />
            <div className="space-y-6">
              <MultiSelectField label={t('admin.products_page.form.color')} name="color" options={existingFilters.color} value={item.color || ""} arValue={item.color_ar || ""} placeholder={isArabic ? "لون جديد" : "New Color"} />
              <MultiSelectField label={t('admin.products_page.form.material')} name="material" options={existingFilters.material} value={item.material || ""} arValue={item.material_ar || ""} placeholder={isArabic ? "مادة جديدة" : "New Material"} />
              <MultiSelectField label={t('admin.products_page.form.dimensions')} name="dimensions" options={existingFilters.dimensions} value={item.dimensions || ""} arValue={item.dimensions_ar || ""} placeholder={isArabic ? "مقاسات جديدة" : "New Dimensions"} />
            </div>
          </section>

          {message && <div className={`p-4 rounded-2xl text-center text-sm font-bold ${message.includes('✅') ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>{message}</div>}

          <div className="pt-6">
            <button type="submit" disabled={loading} className="w-full bg-gray-900 text-white py-6 rounded-[30px] font-black uppercase tracking-[0.2em] text-sm hover:bg-brand-primary active:scale-95 transition-all shadow-xl">
              {loading ? t('admin.common.loading') : t('admin.common.save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
